import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import '@mantine/core/styles.css';
import 'mantine-datatable/styles.layer.css';
import '@mantine/notifications/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/charts/styles.css';

import 'dayjs/locale/zh-tw';

// Import the generated route tree
import { routeTree } from './routeTree.gen'
import { createRouter, RegisteredRouter, RouterProvider, ToPathOption } from '@tanstack/react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createTheme, MantineProvider, v8CssVariablesResolver } from '@mantine/core';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import { ConfirmCountDownModal } from './Modals/ConfirmModal';
import { TextInputModal } from './Modals/TextInputModal';
import { TextareaModal as TextareaModal } from './Modals/TextareaModal';
import { TagsInputModal } from './Modals/TagsInputModal';
import { AutocompleteModal } from './Modals/AutocompleteModal';
import { DatesProvider } from '@mantine/dates';
import { isAxiosError } from 'axios';

// Create a new router instance
const router = createRouter({ routeTree })

// https://github.com/TanStack/router/issues/918#issuecomment-2189234390
export type MyToPathOption = ToPathOption<RegisteredRouter, '/', '/'>;

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

//https://github.com/TanStack/query/discussions/372#discussioncomment-6023276
const MAX_RETRIES = 3;
const HTTP_STATUS_TO_NOT_RETRY = [400, 401, 403, 404];
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (failureCount > MAX_RETRIES) {
          return false;
        }

        if (
          isAxiosError(error) &&
          HTTP_STATUS_TO_NOT_RETRY.includes(error.response?.status ?? 0)
        ) {
          console.log(`Aborting retry due to ${error.response?.status} status`);
          return false;
        }

        return true;
      }
    }
  }
})

const theme = createTheme({
  /** Put your mantine theme override here */
});

const modals = {
  ConfirmCountDown: ConfirmCountDownModal,
  TextInput: TextInputModal,
  Textarea: TextareaModal,
  TagsInput: TagsInputModal,
  Autocomplete: AutocompleteModal,
};
declare module '@mantine/modals' {
  export interface MantineModalsOverride {
    modals: typeof modals;
  }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <MantineProvider theme={theme} cssVariablesResolver={v8CssVariablesResolver}>
        <ModalsProvider modals={modals}>
          <DatesProvider settings={{ locale: 'zh-tw' }}>
            <Notifications />
            <RouterProvider router={router} />
          </DatesProvider>
        </ModalsProvider>
      </MantineProvider>
    </QueryClientProvider>
  </StrictMode>,
)

console.log(`Build:${BUILD_HASH}(${new Date(BUILD_DATE).toLocaleString()})`)